import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import Input from '../../../components/Input';
import Switch from '../../../components/Switch';
import { getIdentityTypeNameError, getIdentityTypeDescriptionError } from '../../../utils/validation';

const AddIdentityTypeModal = ({ open, onClose, onConfirm, identityTypeData, editMode = false }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editMode && identityTypeData) {
      setFormData({
        id: identityTypeData.id,
        name: identityTypeData.name || '',
        description: identityTypeData.description || '',
        isActive: identityTypeData.isActive ?? true
      });
    } else if (!editMode) {
      setFormData({
        name: '',
        description: '',
        isActive: true
      });
    }
    setErrors({});
  }, [editMode, identityTypeData, open]);

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.name.trim() !== '';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('identityTypes.validation.nameRequired');
    } else {
      const nameError = getIdentityTypeNameError(formData.name, t);
      if (nameError) newErrors.name = nameError;
    }

    const descriptionError = getIdentityTypeDescriptionError(formData.description, t);
    if (descriptionError) newErrors.description = descriptionError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Validate field in real-time
    const newErrors = { ...errors };

    if (name === 'name') {
      const nameError = getIdentityTypeNameError(value, t);
      if (nameError) {
        newErrors.name = nameError;
      } else {
        delete newErrors.name;
      }
    } else if (name === 'description') {
      const descriptionError = getIdentityTypeDescriptionError(value, t);
      if (descriptionError) {
        newErrors.description = descriptionError;
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    onConfirm(formData);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      id="identity-type-dialog"
      data-testid="identity-type-dialog"
    >
      <DialogTitle>
        {editMode ? t('identityTypes.editTitle') : t('identityTypes.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Input
            id="name"
            name="name"
            data-testid="identityType-modal-input-name"
            label={t('identityTypes.name')}
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            required
            disabled={loading}
          />
          <Input
            id="description"
            name="description"
            data-testid="identityType-modal-input-description"
            label={t('identityTypes.description')}
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            error={!!errors.description}
            helperText={errors.description}
            disabled={loading}
          />
          <Switch
            id="isActive"
            name="isActive"
            data-testid="identityType-modal-switch-isActive"
            label={t('identityTypes.active')}
            checked={formData.isActive}
            onChange={handleSwitchChange}
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="identityType-modal-button-cancel"
        >
          {t('identityTypes.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '') || loading}
          id="submit"
          name="submit"
          data-testid="identityType-modal-button-submit"
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : editMode ? (
            t('identityTypes.save')
          ) : (
            t('identityTypes.add')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddIdentityTypeModal;
